import {
  Plugin
} from 'ckeditor5'

/**
 * Works around a CKEditor 5 bug, reproduced on ckeditor5-premium-features 44.3.0.
 *
 * `NarrowSidebar` treats any `remove` from the annotation collection as a deletion:
 *
 *   this.listenTo( annotations, 'remove', ( evt, annotation ) => {
 *     ...
 *     annotation === this.activeAnnotation && this.setActiveAnnotation( null );
 *   } );
 *
 * `AnnotationCollection#refreshPositioning()` re-sorts by removing and re-inserting, so a re-sort is
 * indistinguishable from a real deletion. Repositioning runs on every `EditorAnnotations#uiUpdate`,
 * which includes the re-render caused by focusing the annotation just opened, and on a section with
 * enough annotations for the order to change it runs continuously. Two things break:
 *
 *   - opening a comment or suggestion closes it again a moment later, and
 *   - starting a new comment destroys the unsubmitted thread, losing the draft, because
 *     deactivating the annotation sets `isActive = false` and the empty thread is then discarded.
 *
 * The pass only exists to reflow the counter badges, so we skip it entirely while an annotation is
 * open. No re-sort means no `remove`, so nothing is torn down. The badges reflow as soon as the
 * annotation is closed. Trying instead to let the pass run and keep the annotation alive through it
 * does not work: re-opening it afterwards re-renders the editor and triggers another pass, looping
 * forever, and suppressing the teardown calls individually still loses the thread.
 *
 * Only applies once the reader has interacted with the page. CKEditor activates an annotation on
 * load when the caret lands inside one, and that card is meant to close again - skipping the pass
 * from the start would leave a suggestion hanging open every time the section is opened.
 *
 * Scoped to `NarrowSidebar`, which is the only mode the bug has been reported in. `WideSidebar` has
 * the identical handler and the same `refreshPositioning()` call, so it is presumably vulnerable
 * too, but nobody has hit it there - and the guard's cost, annotations not reflowing while one is
 * open, is more visible in the wide sidebar because it aligns full cards to the text rather than
 * small badges. Extend it if the symptom ever shows up at full width.
 */
export default class PreserveAnnotationOnRepositioning extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'PreserveAnnotationOnRepositioning'
	}

	/**
	 * @inheritDoc
	 */
	afterInit() {
		this._readerHasInteracted = false

		this._trackFirstGesture = () => {
			this._readerHasInteracted = true

			document.removeEventListener('mousedown', this._trackFirstGesture, true)
			document.removeEventListener('keydown', this._trackFirstGesture, true)
		}

		document.addEventListener('mousedown', this._trackFirstGesture, true)
		document.addEventListener('keydown', this._trackFirstGesture, true)

		if (this.editor.plugins.has('NarrowSidebar')) {
			this._holdPositioningWhileAnnotationIsOpen(this.editor.plugins.get('NarrowSidebar'))
		}
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		document.removeEventListener('mousedown', this._trackFirstGesture, true)
		document.removeEventListener('keydown', this._trackFirstGesture, true)

		super.destroy()
	}

	_holdPositioningWhileAnnotationIsOpen(annotationsUI) {
		if (typeof annotationsUI._refreshPositioning !== 'function') { return }

		const refreshPositioning = annotationsUI._refreshPositioning.bind(annotationsUI)

		annotationsUI._refreshPositioning = () => {
			if (this._readerHasInteracted && annotationsUI.activeAnnotation) { return }

			return refreshPositioning()
		}
	}
}
